/**
 * Контракти API диска (`/services/disk/...`, `DISK-001` §4.15). Поля — рівно
 * ті, що віддають контролери `backend/web/src/Containers/DiskContainer/UI/API`
 * (групи `disk_node:read` / `disk_node:collection:read`, `disk_storage:read`).
 */

import type { DiskSharedInfo, DiskShareSummary } from './DiskShare'

/** Тип простору — `StorageType` беку. */
export type DiskStorageType = 'personal' | 'company' | 'department' | 'custom'

/** Роль у просторі — `DiskRole` беку, від найслабшої до найсильнішої. */
export type DiskRole = 'viewer' | 'commenter' | 'editor' | 'manager'

/** Вид файлу — `FileKind` беку (папка теж має вид `folder`). */
export type DiskFileKind =
    | 'folder'
    | 'image'
    | 'pdf'
    | 'video'
    | 'audio'
    | 'text'
    | 'document'
    | 'spreadsheet'
    | 'presentation'
    | 'archive'
    | 'other'

/** Права на вузол (`DiskNodeRule`): `write` — вантажити й створювати всередині папки. */
export interface DiskPermissions {
    read?: boolean
    comment?: boolean
    write?: boolean
    update?: boolean
    delete?: boolean
    share?: boolean
    manage?: boolean
}

export interface DiskUserRef {
    uuid?: string
    shortName?: string
    fullName?: string
    firstName?: string
    lastName?: string
    /** Підписане посилання на фото (`UserEntity::getPhotoUrl`); немає фото — поля немає, аватар з ініціалами. */
    photoUrl?: string
}

export interface DiskStorageSettings {
    editorsCanShare: boolean
    versionsToKeep: number
    trashDays: number
}

export interface DiskStorage {
    uuid: string
    type: DiskStorageType
    title: string
    role: DiskRole | null
    rootFolderUuid: string
    usedBytes: number
    effectiveQuotaBytes: number | null
    settings: DiskStorageSettings | null
}

export interface DiskStorageRef {
    uuid: string
    type: DiskStorageType
    title: string
}

export interface DiskAncestor {
    uuid: string
    name: string
    root: boolean
}

export interface DiskFileVersion {
    uuid: string
    number: number
    size: number
    mime: string | null
    createdAt: string | null
}

export interface DiskNode {
    uuid: string
    type: 'folder' | 'file'
    name: string
    size: number
    mime: string | null
    kind: DiskFileKind
    storage: DiskStorageRef | null
    parentUuid: string | null
    createdAt: string | null
    createdBy: DiskUserRef | null
    updatedAt: string | null
    updatedBy: DiskUserRef | null
    role: DiskRole | null
    /**
     * Шлях від кореня простору до батька, без самого вузла: крихти картки й
     * «де лежить» у пошуку, нещодавніх, списку папки й кошику (`NodeAncestors`
     * беку — один запит на сторінку).
     */
    ancestors: DiskAncestor[] | null
    /** Лише картка файлу. */
    currentVersion: DiskFileVersion | null
    /** Лише кошик. */
    deletedAt: string | null
    deletedBy: DiskUserRef | null
    originalParentUuid: string | null
    originalParentName: string | null
    /** Доступи, надані на самому вузлі (значок «є доступи»); `null` — немає. */
    shareSummary: DiskShareSummary | null
    /** Роль на вузлі дав доступ, а не простір (значок «спільне з вами»). */
    sharedWithMe: boolean
    /** Лише «Спільні зі мною»: хто й коли поділився. */
    shared: DiskSharedInfo | null
    permissions: DiskPermissions
}

export interface DiskCollection<T> {
    items: T[]
    total: number
}

export interface DiskQuotaLine {
    uuid: string | null
    title: string
    type: DiskStorageType | null
    quotaBytes: number | null
    usedBytes: number
    reservedBytes: number
    percent: number | null
}

export interface DiskQuota {
    personal: DiskQuotaLine | null
    managed: DiskQuotaLine[]
}

export interface DiskHistoryItem {
    uuid: string
    event: string
    payload: Record<string, unknown>
    changedBy: DiskUserRef | null
    occurredAt: string | null
}

export interface DiskContentUrl {
    url: string
    expiresAt: string | null
}

export type DiskSortField = 'name' | 'updatedAt' | 'size'
export type DiskSortOrder = 'asc' | 'desc'

export interface DiskSort {
    field: DiskSortField
    order: DiskSortOrder
}

export type DiskView = 'list' | 'grid'

/** Що відкрито в правій частині екрана. */
export type DiskLocation =
    | { kind: 'folder'; storageUuid: string; folderUuid: string | null }
    | { kind: 'recent' }
    | { kind: 'trash'; storageUuid: string }
    /** «Спільні зі мною»: `folderUuid = null` — перелік спільних, інакше — папка всередині спільної. */
    | { kind: 'shared'; folderUuid: string | null }

/**
 * Межа простого завантаження одним запитом, МіБ — `DiskUploadPolicy::CHUNK_SIZE`
 * (= `SIMPLE_MAX_BYTES`). Більший файл бек відхилить; його повезе менеджер
 * завантажень шматками (наряд Д1.10). Пару тримає вартовий «константи,
 * скопійовані з беку».
 */
export const DISK_SIMPLE_UPLOAD_MAX_MIB = 8

/** Мінімальна довжина пошукового запиту — `GetDiskSearchCollectionAction::MIN_QUERY`. */
export const DISK_SEARCH_MIN_QUERY = 2

/** Поріг попередження в картці квоти, % — той самий, що в першому сповіщенні беку. */
export const DISK_QUOTA_WARN_PERCENT = 80
